import { config } from '../config';

export const authService = {
  /**
   * Login user with email and password
   * @returns {Object} Response with tokens and user data, or challenge info
   */
  login: async (email, password) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.message || error.error?.message || error.error || 'Login failed');
      err.code = error.code;
      throw err;
    }

    return response.json();
  },

  resendVerification: async (email) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/resendverification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName: email })
    });

    const data = await response.json().catch(() => ({}));
    return data;
  },

  /**
   * Logout user (global sign out).
   *
   * THE TOKEN SAYS WHO IS LOGGING OUT. The server ignores any username in the body and signs out the
   * bearer of this token — it used to take the body's word for it, which let anyone who knew an email
   * address end that person's sessions without logging in at all (HP-118). Nothing to send but the token.
   */
  logout: async () => {
    const response = await fetch(`${config.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('idToken') || ''
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'Logout failed');
    }

    return response.json();
  },

  /**
   * Refresh authentication tokens
   * @param {string} refreshToken - Refresh token
   */
  refreshToken: async (refreshToken) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'Token refresh failed');
    }

    return response.json();
  },

  /**
   * Get current session/validate token
   * @param {string} idToken - ID token to validate
   */
  getSession: async (idToken) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/session`, {
      method: 'GET',
      headers: {
        'Authorization': idToken,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'Session validation failed');
    }

    return response.json();
  },

  /**
   * Set password for NEW_PASSWORD_REQUIRED challenge
   * @param {string} username - Username
   * @param {string} newPassword - New password to set
   * @param {string} session - Session token from login challenge
   */
  setPassword: async (username, newPassword, session) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/password/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, newPassword, session })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'Password set failed');
    }

    return response.json();
  },

  /**
   * Change your own password (requires the old one).
   *
   * Whose password changes is decided by the token, not by a username in the body (HP-122) — so the
   * caller sends the token and the two passwords, and nothing else.
   *
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   */
  changePassword: async (oldPassword, newPassword) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/password/change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('idToken') || ''
      },
      body: JSON.stringify({ oldPassword, newPassword })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'Password change failed');
    }

    return response.json();
  },

  forgotPasswordRequest: async (email) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/forgetpassword`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName: email })
    });

    const data = await response.json().catch(() => ({}));
    if (!data.success) {
      throw new Error(data.message || 'Failed to send reset code');
    }
    return data;
  },

  confirmForgotPassword: async (email, code, password) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/forgetpassword`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName: email, code, password })
    });

    const data = await response.json().catch(() => ({}));
    if (!data.success) {
      const err = new Error(data.message || 'Failed to reset password');
      err.code = data.code;
      throw err;
    }
    return data;
  },

  respondCognitoChallenge: async (challengeName, username, session, code) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/challenge/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeName, username, session, code })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.message || 'Verification failed');
    }
    return response.json();
  },

  verifyMfa: async (mfaSessionId, otp) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfaSessionId, otp })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.message || 'Verification failed');
    }

    return response.json();
  },

  resendMfaCode: async (mfaSessionId) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/mfa/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfaSessionId })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.message || 'Failed to resend code');
    }

    return response.json();
  },

  /**
   * Exchange Cognito OAuth authorization code for tokens (used after SSO redirect)
   * @param {string} code - Authorization code from Cognito hosted UI callback
   */
  exchangeSsoCode: async (code) => {
    const response = await fetch(`${config.apiBaseUrl}/auth/sso/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || error.message || 'SSO token exchange failed');
    }

    return response.json();
  }
};
